import { useEffect } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS, useJoyride } from 'react-joyride';
import type { Step, EventData, Controls } from 'react-joyride';

const tourSteps: Step[] = [
  // ── Welcome ──
  {
    target: 'body',
    placement: 'center',
    skipBeacon: true,
    title: '👋 Welcome to CoTaskr!',
    content:
      'This quick guide walks you through every major feature so you can hit the ground running. Let\'s get started!',
  },

  // ── Sidebar Nav ──
  {
    target: '[data-tour="sidebar"]',
    placement: 'right',
    title: 'Sidebar Navigation',
    content:
      'The sidebar is organized by how you use the app: Home (overview & docs), Work (tasks, timelines & sprints—including Gantt), Schedule (calendar, time & workload), AI tools, Insights, Team, and Admin.',
  },

  // ── Dashboard ──
  {
    target: '[data-tour="nav-dashboard"]',
    placement: 'right',
    title: 'Dashboard',
    content:
      'Your personal overview — see total tasks, completion rate, overdue items, weekly progress charts, and recent activity at a glance.',
  },

  // ── Projects ──
  {
    target: '[data-tour="nav-projects"]',
    placement: 'right',
    title: 'Projects',
    content:
      'Create and manage projects. Each project has its own board, backlog, sprints, and settings. Assign members and set deadlines here.',
  },

  // ── Board ──
  {
    target: '[data-tour="nav-board"]',
    placement: 'right',
    title: 'Kanban Board',
    content:
      'Drag-and-drop tasks across columns (To Do → In Progress → Review → Done). Filter by assignee, priority, or label.',
  },

  // ── Backlog ──
  {
    target: '[data-tour="nav-backlog"]',
    placement: 'right',
    title: 'Backlog',
    content:
      'All unscheduled tasks live here. Prioritize, estimate, and drag items into sprints when ready.',
  },

  // ── Sprints ──
  {
    target: '[data-tour="nav-sprints"]',
    placement: 'right',
    title: 'Sprint Master',
    content:
      'Plan time-boxed iterations. Start a sprint, track velocity, and review burndown charts at the end.',
  },

  // ── List View ──
  {
    target: '[data-tour="nav-list"]',
    placement: 'right',
    title: 'List View',
    content:
      'A table-style view of all tasks with inline editing. Great for bulk updates — sort, filter, and group by any field.',
  },

  // ── Calendar ──
  {
    target: '[data-tour="nav-calendar"]',
    placement: 'right',
    title: 'Calendar',
    content:
      'See tasks and deadlines on a month/week/day calendar. Drag to reschedule.',
  },

  // ── Gantt ──
  {
    target: '[data-tour="nav-gantt"]',
    placement: 'right',
    title: 'Gantt Chart',
    content:
      'Visualize project timelines with dependency arrows. Resize bars to adjust durations.',
  },

  // ── Workload ──
  {
    target: '[data-tour="nav-workload"]',
    placement: 'right',
    title: 'Workload',
    content:
      'See how work is distributed across team members. Spot over-allocation and rebalance assignments.',
  },

  // ── Time Tracking ──
  {
    target: '[data-tour="nav-time-tracking"]',
    placement: 'right',
    title: 'Time Tracking',
    content:
      'Start/stop timers on tasks, log manual entries, and review time reports per user or project.',
  },

  // ── Reports ──
  {
    target: '[data-tour="nav-reports"]',
    placement: 'right',
    title: 'Reports',
    content:
      'Generate project, sprint, team, velocity, and workload reports. Export charts as images or PDF.',
  },

  // ── Stakeholder View ──
  {
    target: '[data-tour="nav-stakeholder"]',
    placement: 'right',
    title: 'Stakeholder View',
    content:
      'A read-only executive dashboard for stakeholders — high-level progress without the clutter.',
  },

  // ── AI Studio ──
  {
    target: '[data-tour="nav-ai-studio"]',
    placement: 'right',
    title: 'AI Studio',
    content:
      'Use AI to auto-generate tasks, get smart suggestions, estimate effort, and analyze project risks.',
  },

  // ── Members ──
  {
    target: '[data-tour="nav-members"]',
    placement: 'right',
    title: 'Org members',
    content:
      'Under Admin → Org members: invite people and set org-wide roles. Then open Projects → Teams on any project to assign them.',
  },

  // ── Header bar ──
  {
    target: '[data-tour="header-search"]',
    placement: 'bottom',
    title: 'Global Search',
    content:
      'Press ⌘K (or Ctrl+K) to quickly find tasks, projects, or people across your entire workspace.',
  },

  {
    target: '[data-tour="header-notifications"]',
    placement: 'bottom',
    title: 'Notifications',
    content:
      'Click the bell for the latest alerts. Use Open notification center at the bottom of the panel for the full inbox and history (/notifications).',
  },

  {
    target: '[data-tour="header-ai-assistant"]',
    placement: 'bottom',
    title: 'AI Assistant',
    content:
      'Open AI Assistant from the header to chat, ask project questions, and get fast summaries.',
  },

  {
    target: '[data-tour="header-settings"]',
    placement: 'bottom',
    title: 'Settings',
    content:
      'Access profile settings, theme preferences, notification settings, and workspace configuration.',
  },

  // ── Admin (conditional — targets exist only for admins) ──
  {
    target: '[data-tour="nav-admin"]',
    placement: 'right',
    title: 'Admin Panel',
    content:
      'Organization management — tenant settings, billing, SSO config, usage analytics, and user administration. (Admin only)',
  },

  {
    target: '[data-tour="nav-integrations"]',
    placement: 'right',
    title: 'Integrations',
    content:
      'Connect external tools — GitHub, Jira, Slack, Discord, Trello, Linear, and more. Sync data automatically.',
  },

  {
    target: '[data-tour="nav-webhooks"]',
    placement: 'right',
    title: 'Webhooks',
    content:
      'Set up webhook endpoints to send or receive events. Automate workflows across your toolchain.',
  },

  {
    target: '[data-tour="nav-workflows"]',
    placement: 'right',
    title: 'Workflows',
    content:
      'Build custom automation rules — auto-assign, auto-transition, send notifications when conditions are met.',
  },

  {
    target: '[data-tour="nav-roles"]',
    placement: 'right',
    title: 'Roles & Permissions',
    content:
      'Create custom roles with fine-grained permissions. Control who can view, create, edit, or delete resources.',
  },

  // ── Finish ──
  {
    target: 'body',
    placement: 'center',
    skipBeacon: true,
    title: '🎉 You\'re all set!',
    content:
      'That covers the main features of CoTaskr. You can re-open this guide anytime from the "?" button in the header. Happy project managing!',
  },
];

interface UserGuideTourProps {
  run: boolean;
  onFinish: () => void;
}

export default function UserGuideTour({ run, onFinish }: UserGuideTourProps) {
  const { controls, state, Tour } = useJoyride({
    steps: tourSteps,
    continuous: true,
    scrollToFirstStep: true,
    locale: {
      back: 'Back',
      close: 'Close',
      last: 'Finish',
      next: 'Next',
      skip: 'Skip Tour',
    },
    options: {
      showProgress: true,
      buttons: ['skip', 'back', 'primary', 'close'],
      primaryColor: '#1D4ED8',
      overlayColor: 'rgba(0, 0, 0, 0.45)',
      zIndex: 10000,
    },
    styles: {
      tooltip: {
        borderRadius: '12px',
        padding: '20px',
        fontSize: '14px',
      },
      tooltipTitle: {
        fontSize: '17px',
        fontWeight: 700,
        marginBottom: '6px',
      },
      tooltipContent: {
        fontSize: '13.5px',
        lineHeight: '1.6',
        color: '#374151',
      },
      buttonPrimary: {
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 600,
        padding: '8px 18px',
      },
      buttonBack: {
        color: '#6B7280',
        fontSize: '13px',
        fontWeight: 500,
      },
      buttonSkip: {
        color: '#9CA3AF',
        fontSize: '12px',
      },
    },
    onEvent: (data: EventData, ctrl: Controls) => {
      const { status, action, type, index } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        onFinish();
        return;
      }

      // Auto-skip steps whose target doesn't exist in the DOM
      if (type === EVENTS.TARGET_NOT_FOUND) {
        ctrl.next();
      }
    },
  });

  // Start/stop the tour when the run prop changes
  useEffect(() => {
    if (run) {
      controls.start();
    } else if (state.status === STATUS.RUNNING) {
      controls.stop();
    }
  }, [run]);

  return <>{Tour}</>;
}
